import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Truck, SlidersHorizontal, X } from "lucide-react";
import { API, PRODUCT_BANNER_IMAGE } from "../constants";
import { useCart } from "../context/CartContext";
import WishlistButton from "../components/WishlistButton";

const SORT_OPTIONS = [
  { value: "", label: "Newest" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "name-asc", label: "Name: A → Z" },
  { value: "name-desc", label: "Name: Z → A" },
];

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sort, setSort] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get('category');
    if (cat) setSelectedCategory(cat);
    const br = params.get('brand');
    if (br) setSelectedBrand(br);
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          category: selectedCategory || undefined,
          brand: selectedBrand || undefined,
          search: searchQuery || undefined,
          min_price: minPrice ? Number(minPrice) : undefined,
          max_price: maxPrice ? Number(maxPrice) : undefined,
          sort: sort || undefined,
          in_stock: inStockOnly ? true : undefined,
        };
        const [productsRes, categoriesRes, brandsRes] = await Promise.all([
          axios.get(`${API}/products`, { params }),
          axios.get(`${API}/products/categories`),
          axios.get(`${API}/products/brands`),
        ]);
        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setBrands(brandsRes.data);
      } catch (error) {
        console.error('Failed to fetch products');
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedCategory, selectedBrand, searchQuery, minPrice, maxPrice, sort, inStockOnly]);

  const activeFilterCount =
    (selectedCategory ? 1 : 0) +
    (selectedBrand ? 1 : 0) +
    (searchQuery ? 1 : 0) +
    (minPrice ? 1 : 0) +
    (maxPrice ? 1 : 0) +
    (sort ? 1 : 0) +
    (inStockOnly ? 1 : 0);

  const clearAll = () => {
    setSelectedCategory('');
    setSelectedBrand('');
    setSearchQuery('');
    setMinPrice('');
    setMaxPrice('');
    setSort('');
    setInStockOnly(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <section className="mb-10">
          <div className="w-full h-48 sm:h-64 lg:h-72 rounded-sm overflow-hidden relative">
            <img src={PRODUCT_BANNER_IMAGE} alt="Cut Above Polish Shop Products" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-black/70 flex items-center px-6 sm:px-10">
              <div>
                <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#D62828] mb-1">Cut Above Polish Shop</p>
                <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-wide mb-2">METAL POLISHING & AUTOMOTIVE DETAILING SUPPLIES</h1>
                <p className="text-gray-300 text-sm sm:text-base max-w-xl">Everything you need to cut, color, and finish — trusted by polishers and detailers nationwide.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Vendor / brand filter chips */}
        {brands.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="brand-chips">
            <span className="text-xs uppercase tracking-widest text-gray-500 mr-1">Brand:</span>
            <button
              type="button"
              onClick={() => setSelectedBrand('')}
              data-testid="brand-chip-all"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border transition-all ${selectedBrand === '' ? 'bg-[#D62828] border-[#D62828] text-white' : 'bg-transparent border-white/15 text-gray-400 hover:border-white/40 hover:text-white'}`}
            >
              All ({brands.reduce((n, b) => n + b.count, 0)})
            </button>
            {brands.map((b) => (
              <button
                key={b.vendor}
                type="button"
                onClick={() => setSelectedBrand(selectedBrand === b.vendor ? '' : b.vendor)}
                data-testid={`brand-chip-${b.vendor.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide border transition-all ${selectedBrand === b.vendor ? 'bg-[#D4AF37] border-[#D4AF37] text-black' : 'bg-transparent border-white/15 text-gray-300 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]'}`}
              >
                {b.vendor} <span className="opacity-60">({b.count})</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 mb-4" data-testid="product-filters">
          <input
            type="text"
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 focus:border-[#D62828] rounded-sm h-12 px-4 flex-1"
            data-testid="search-input"
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-[#0F0F0F] border border-white/10 text-white rounded-sm h-12 px-4 min-w-[200px]"
            data-testid="category-select"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className={`flex items-center justify-center gap-2 h-12 px-4 rounded-sm border transition-colors ${
              filtersOpen || activeFilterCount > 0
                ? "bg-[#D62828] border-[#D62828] text-white"
                : "bg-[#0F0F0F] border-white/10 text-gray-300 hover:border-white/30"
            }`}
            data-testid="advanced-filters-toggle"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span className="text-sm font-semibold">Filters</span>
            {activeFilterCount > 0 && (
              <span className="ml-1 bg-white text-[#D62828] text-xs font-bold rounded-full w-5 h-5 inline-flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {filtersOpen && (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-8" data-testid="advanced-filters-panel">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-wide mb-1">Min price ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm h-10 px-3 text-white text-sm focus:border-[#D62828] focus:outline-none"
                  data-testid="min-price-input"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-wide mb-1">Max price ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="500"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm h-10 px-3 text-white text-sm focus:border-[#D62828] focus:outline-none"
                  data-testid="max-price-input"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-wide mb-1">Sort by</label>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm h-10 px-3 text-white text-sm"
                  data-testid="sort-select"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col justify-end">
                <label className="flex items-center gap-2 cursor-pointer h-10 px-3 bg-[#0A0A0A] border border-white/10 rounded-sm" data-testid="in-stock-only">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="w-4 h-4 accent-[#D62828]"
                  />
                  <span className="text-sm text-gray-300">In stock only</span>
                </label>
              </div>
            </div>
            {activeFilterCount > 0 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-gray-500 text-xs">
                  {products.length} result{products.length === 1 ? '' : 's'} with {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'}
                </p>
                <button
                  onClick={clearAll}
                  className="text-xs text-[#D62828] hover:text-white inline-flex items-center gap-1 uppercase tracking-wide font-bold"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-3 h-3" /> Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1,2,3,4,5,6,7,8,9,10].map((i) => (
              <div key={i} className="bg-[#121212] rounded-sm animate-pulse">
                <div className="aspect-[4/3] bg-[#1A1A1A]"></div>
                <div className="p-3 space-y-2">
                  <div className="h-2 bg-[#1A1A1A] rounded w-1/3"></div>
                  <div className="h-3 bg-[#1A1A1A] rounded w-2/3"></div>
                  <div className="h-4 bg-[#1A1A1A] rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="products-grid">
            {products.map((product) => (
              <Link to={`/products/${product.product_id}`} key={product.product_id} className="product-card rounded-sm overflow-hidden group relative" data-testid={`product-card-${product.product_id}`}>
                <div className="absolute top-2 right-2 z-10">
                  <WishlistButton productId={product.product_id} size="sm" showTooltip={false} />
                </div>
                <div className="aspect-[4/3] overflow-hidden bg-[#1A1A1A]">
                  <img src={product.image_url || 'https://images.unsplash.com/photo-1692486407015-3a47e4c1181c'} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-3">
                  <p className="text-gray-500 text-[10px] uppercase tracking-wide mb-0.5">{product.category}</p>
                  <h3 className="font-semibold text-white text-sm mb-1 line-clamp-2 leading-tight">{product.name}</h3>
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[#D62828] font-bold text-base">${product.online_price.toFixed(2)}</span>
                      {product.msrp && product.msrp > product.online_price && (
                        <span className="text-gray-500 text-[11px] line-through" data-testid={`product-msrp-${product.product_id}`}>${product.msrp.toFixed(2)}</span>
                      )}
                    </div>
                    <button onClick={(e) => { e.preventDefault(); addToCart(product.product_id); }} className="bg-white/10 hover:bg-[#D62828] p-1.5 rounded-sm transition-colors" data-testid={`add-to-cart-${product.product_id}`}>
                      <ShoppingCart className="w-4 h-4" />
                    </button>
                  </div>
                  {product.fulfillment === 'Stock' && (
                    <p className="text-[10px] text-green-500 mt-1 flex items-center gap-1">
                      <Truck className="w-3 h-3" /> Ships from warehouse
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">No products found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
